import { ROUTES } from '#/config/routes';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { searchStockIssueCategoryFilterOptions } from '#/api/system/stockIssueCategory/fetchSelectOptions';
import {
  readIssueDateRangeFilter,
  resolveIssueDateFilterChipDisplay,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/issueDateListFilter';
import {
  STOCK_ISSUE_STATUS_FILTER_OPTIONS,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/issueStatus';
import { formatDateOrDash } from '#/utils/date';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function resolveCategoryLabel(category) {
  if (!category) {
    return '-';
  }

  return [category.code, category.name].filter(Boolean).join(' ') || '-';
}

export function buildStockIssueColumns({
  filters = {},
  onFilterChange,
  onIssueDateRangeChange,
  categoryLabelCache,
  canShowEdit,
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.stockIssueDetail(row.id),
      getEditPath: (row) => ROUTES.stockIssueEdit(row.id),
      canShowEdit,
    }),
    {
      id: 'issueNo',
      header: '出庫單號',
      accessorKey: 'issueNo',
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'issueNo',
        label: '出庫單號',
        filterValue: filters.issueNo,
        onChange: (value) => onFilterChange('issueNo', value),
        placeholder: '搜尋出庫單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <DocumentStatusBadge status={row.original.status} size="sm" />
      ),
      size: 96,
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: STOCK_ISSUE_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      header: '出庫日',
      accessorKey: 'issueDate',
      accessorFn: (row) => formatDateOrDash(row.issueDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'issueDateFrom',
        label: '出庫日',
        mode: 'range',
        filterValue: readIssueDateRangeFilter(filters),
        onChange: onIssueDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveIssueDateFilterChipDisplay,
      }),
    },
    {
      id: 'category',
      header: '出庫類別',
      accessorFn: (row) => resolveCategoryLabel(row.category),
      size: 160,
      minSize: 140,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'stockIssueCategoryId',
          label: '出庫類別',
          searchOptions: searchStockIssueCategoryFilterOptions,
          filterValue: filters.stockIssueCategoryId,
          onChange: (value) => onFilterChange('stockIssueCategoryId', value),
          resolveFilterDisplay: (values) => resolveFromLabelCache(categoryLabelCache, values),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(categoryLabelCache, options, values);
          },
        }),
      },
    },
    {
      header: '備註',
      accessorKey: 'remark',
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original?.remark || '—'} />
      ),
    },
    {
      id: 'lineCount',
      header: '物料項目數',
      accessorFn: (row) => {
        if (Array.isArray(row.lines)) {
          return row.lines.length;
        }
        if (row.lineCount != null && row.lineCount !== '') {
          return row.lineCount;
        }
        return '—';
      },
      size: 100,
      minSize: 100,
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
    },
  ];
}
